import type { TEventQuery } from './api';

export const FACET_GROUPS: { title: string; facets: { field: TFacetField; label: string }[] }[] = [
  {
    title: 'Route',
    facets: [
      { field: 'sourceId', label: 'Source' },
      { field: 'destinationId', label: 'Destination' },
      { field: 'customerId', label: 'Customer' },
    ],
  },
  {
    title: 'Grouping',
    facets: [{ field: 'issueId', label: 'Issue' }],
  },
  {
    title: 'Outcome',
    facets: [
      { field: 'status', label: 'Status' },
      { field: 'responseStatus', label: 'Response code' },
      { field: 'responseStatusClass', label: 'Response class' },
      { field: 'errorClass', label: 'Error class' },
      { field: 'attempts', label: 'Attempts' },
    ],
  },
  {
    title: 'Identity',
    facets: [
      { field: 'eventType', label: 'Event type' },
      { field: 'origin', label: 'Origin' },
      { field: 'authResult', label: 'Sender check' },
    ],
  },
];

export type TFacetField =
  | 'status'
  | 'sourceId'
  | 'destinationId'
  | 'customerId'
  | 'responseStatus'
  | 'responseStatusClass'
  | 'errorClass'
  | 'attempts'
  | 'eventType'
  | 'origin'
  | 'issueId'
  | 'authResult';

export const FACET_LABEL: Record<TFacetField, string> = Object.fromEntries(
  FACET_GROUPS.flatMap((group) => group.facets).map((facet) => [facet.field, facet.label]),
) as Record<TFacetField, string>;

// Only these can be asked of `/v1/events/facets`; the rest have no index.
export const COUNTABLE_FACETS = new Set<TFacetField>([
  'status',
  'sourceId',
  'destinationId',
  'customerId',
  'eventType',
  'responseStatus',
  'errorClass',
  'attempts',
  'origin',
  'issueId',
  'authResult',
]);

export const NAMED_FACETS = new Set<TFacetField>([
  'sourceId',
  'destinationId',
  'customerId',
  'issueId',
]);

export const FACET_VALUE_WORD: Partial<Record<TFacetField, Record<string, string>>> = {
  origin: {
    ingest: 'Sent to us',
    replay: 'Replayed by hand',
    recover: 'Recovered: re-sent through its flow',
  },
  // `attempts` values are buckets, not counts: `first | retrying | exhausted | none`.
  attempts: {
    none: 'Never attempted',
    first: 'Delivered first try',
    retrying: 'Still retrying',
    exhausted: 'Budget spent',
  },
  errorClass: {
    status_5xx: 'Destination error · 5xx',
    conn_refused: 'Connection refused',
    tls: 'TLS handshake failed',
  },
  authResult: {
    verified: 'Verified',
    failed: 'Unverified — kept anyway',
    skipped: 'Not checked',
  },
  responseStatusClass: {
    '2xx': '2xx · accepted',
    '3xx': '3xx · redirected',
    '4xx': '4xx · rejected',
    '5xx': '5xx · destination error',
  },
};

export interface IEventFilters {
  minutes?: number;
  from?: string;
  to?: string;
  search?: string;
  // One request, all the events it produced. Not a facet: the API takes a single id, and a
  // link that carried it in the URL was being parsed away and silently answered with everything.
  requestId?: string;
  values: Partial<Record<TFacetField, string[]>>;
}

export const PERIODS: { minutes: number; label: string }[] = [
  { minutes: 60, label: '1h' },
  { minutes: 6 * 60, label: '6h' },
  { minutes: 24 * 60, label: '24h' },
  { minutes: 7 * 24 * 60, label: '7d' },
  { minutes: 30 * 24 * 60, label: '30d' },
];

export const DEFAULT_MINUTES = 24 * 60;

export const resolveWindow = (filters: IEventFilters, now = Date.now()) => {
  const to = filters.to ?? new Date(now).toISOString();
  if (filters.from) return { from: filters.from, to };
  const minutes = filters.minutes ?? DEFAULT_MINUTES;
  return { from: new Date(now - minutes * 60_000).toISOString(), to };
};

export const toQuery = (filters: IEventFilters, now?: number): TEventQuery => {
  const window = resolveWindow(filters, now);
  // Both ends always: the server answers 400 to an open `to`.
  const query: Record<string, unknown> = { from: window.from, to: window.to };
  if (filters.search) query.search = filters.search;
  if (filters.requestId) query.requestId = filters.requestId;
  for (const [field, values] of Object.entries(filters.values)) {
    if (values && values.length > 0) query[field] = values;
  }
  return query as TEventQuery;
};

export const activeCount = (filters: IEventFilters): number =>
  Object.values(filters.values).reduce((sum, values) => sum + (values?.length ?? 0), 0) +
  (filters.requestId ? 1 : 0);

export const withValues = (
  filters: IEventFilters,
  field: TFacetField,
  values: string[],
): IEventFilters => {
  const next = { ...filters.values };
  if (values.length === 0) delete next[field];
  else next[field] = values;
  return { ...filters, values: next };
};

export const parseFilters = (search: Record<string, unknown>): IEventFilters => {
  const text = (key: string) =>
    typeof search[key] === 'string' ? (search[key] as string) : undefined;
  const list = (key: string): string[] | undefined => {
    const raw = search[key];
    if (Array.isArray(raw)) return raw.map(String);
    if (typeof raw === 'string' && raw.length > 0) return raw.split(',');
    return undefined;
  };

  const values: Partial<Record<TFacetField, string[]>> = {};
  for (const field of Object.keys(FACET_LABEL) as TFacetField[]) {
    const parsed = list(field);
    if (parsed) values[field] = parsed;
  }

  const minutes = Number(search.minutes);
  return {
    minutes: Number.isFinite(minutes) && minutes > 0 ? minutes : undefined,
    from: text('from'),
    to: text('to'),
    search: text('q'),
    requestId: text('requestId'),
    values,
  };
};

export const filtersToSearch = (filters: IEventFilters): Record<string, string | number> => {
  const out: Record<string, string | number> = {};
  if (filters.minutes) out.minutes = filters.minutes;
  if (filters.from) out.from = filters.from;
  if (filters.to) out.to = filters.to;
  if (filters.search) out.q = filters.search;
  if (filters.requestId) out.requestId = filters.requestId;
  for (const [field, values] of Object.entries(filters.values)) {
    if (values && values.length > 0) out[field] = values.join(',');
  }
  return out;
};
